import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Source, useMap } from 'shared/map-exports';
import { ON_TOP_OF_EVERYTHING_LAYER_ID } from 'features/mapping/styles/constants';
import { useQuery } from '@tanstack/react-query';
import { usePoisState } from 'state/usePoisState';
import { usePrevious } from 'hooks/usePrevious';
import { AircraftDetails } from 'features/mapping/mapping.types';
import { isEqual } from 'es-toolkit';
import { hasValidCoordinates } from 'shared/utils';
import {
  aircraftsToGeoJson,
  firefightAirCraftsToObject,
  mergeDataToCache,
} from './utils';
import {
  fetchAircraftTraces,
  fetchAircraftsByHexCode,
  fetchFirefightingAircrafts,
  fetchActiveAircrafts,
} from './api';
import {
  ADSB_FETCH_INTERVAL_MS,
  DEFAULT_LAYER_TIMEOUT_MS,
  AIRCRAFT_LOCATION_REFRESH_INTERVAL_MS,
  AIRCRAFT_TRACE_REFRESH_INTERVAL_MS,
  TRACE_SOURCE_ID,
  TRACE_LAYER_ID,
} from './constants';
import {
  AircraftCache,
  AircraftCacheNoData,
  AircraftLayerFeature,
  FirefightingAircraft,
} from './types';
import { traceStyle } from './AircraftsLayer.styles';
import { AircraftDeckLayers } from './AircraftDeckLayers';

type AircraftsLayerDeckGlProps = {
  visible: boolean;
};

export const AircraftsLayerDeckGl = (
  props: AircraftsLayerDeckGlProps,
): JSX.Element | null => {
  const { visible } = props;
  const { current: map } = useMap();

  const { selectedPoi, setSelectedSingletonPoi, clearSelectedPois } =
    usePoisState();
  const [hoveredAircraftHexCode, setHoveredAircraftHexCode] = useState<
    string | null
  >(null);
  const [selectedAircraftHexCode, setSelectedAircraftHexCode] = useState('');

  // Hex codes whose labels are always shown (regardless of zoom). Baked into
  // the label style so it regenerates on hover/selection change.
  const activeLabelHexCodes = useMemo(
    () =>
      [hoveredAircraftHexCode, selectedAircraftHexCode].filter(
        Boolean,
      ) as string[],
    [hoveredAircraftHexCode, selectedAircraftHexCode],
  );

  // this acts as a local cache of the active aircrafts query- with us updating the values within it but keeping the reference
  // to planes that are no longer included in the ADSB response for periods of time.
  const [activePlanesCache, setActivePlanesCache] =
    useState<AircraftCacheNoData>({});

  // this acts as a local cache of the aircraft final data - with us updating the values within it but keeping the reference
  // to planes that are no longer included in the ADSB response for periods of time.
  const [planePositionCache, setPlanePositionCache] = useState<AircraftCache>(
    {},
  );

  const selectedAircraftPoi =
    selectedPoi?.type === 'aircraft' ? selectedPoi : null;
  const prevSelectedAircraftPoi = usePrevious(selectedAircraftPoi);

  useEffect(() => {
    if (prevSelectedAircraftPoi && !selectedAircraftPoi) {
      setSelectedAircraftHexCode('');
    }
  }, [prevSelectedAircraftPoi, selectedAircraftPoi]);

  const handleHover = useCallback((item: AircraftLayerFeature | null) => {
    setHoveredAircraftHexCode(item?.properties.hex || null);
  }, []);

  const handleClick = useCallback(
    (item: AircraftLayerFeature) => {
      if (!map) return;
      setSelectedAircraftHexCode(item.properties.hex);

      const aircraft = {
        name: item.properties.name,
        model: item.properties.model,
        tailNum: item.properties.tailNum,
        type: item.properties.type,
        hex: item.properties.hex,
        altitude: item.properties.altitude,
        speed: item.properties.speed,
        squawk: item.properties.squawk,
      };

      setSelectedSingletonPoi({
        type: 'aircraft',
        id: aircraft.hex,
        metadata: aircraft,
      });
    },
    [map, setSelectedSingletonPoi],
  );

  const dbQuery = useQuery({
    queryKey: ['firefighting-aircrafts', visible],
    queryFn: () => (visible ? fetchFirefightingAircrafts() : []),
    staleTime: DEFAULT_LAYER_TIMEOUT_MS,
  });

  const dbData = useMemo(() => dbQuery.data || [], [dbQuery.data]);

  const activeDataQuery = useQuery({
    queryKey: ['active-aircraft'],
    queryFn: () => fetchActiveAircrafts(dbData, true /* useVendorCdn */),
    refetchInterval: ADSB_FETCH_INTERVAL_MS,
    // If visible and we have the data for aircrafts from our db fetched
    enabled: visible && Boolean(dbData.length),
  });
  const { data: activeData } = activeDataQuery;

  useEffect(() => {
    if (!activeData) return;
    setActivePlanesCache((cached) =>
      mergeDataToCache(activeData, cached, false),
    );
  }, [activeData]);

  const hexList = useMemo(
    () => Object.keys(activePlanesCache),
    [activePlanesCache],
  );
  const activeMetadata = useMemo(() => {
    if (!dbData.length || !hexList) return {};

    const dbAircrafts = firefightAirCraftsToObject(dbData);
    return hexList.reduce(
      (acc, hex) => {
        const aircraftMetadata = dbAircrafts[hex];
        if (aircraftMetadata) {
          acc[hex] = aircraftMetadata;
        }
        return acc;
      },
      {} as Record<string, FirefightingAircraft>,
    );
  }, [hexList, dbData]);

  const filteredAircraftsQuery = useQuery({
    queryKey: ['filteredAircrafts'],
    queryFn: () =>
      fetchAircraftsByHexCode(hexList.join(','), true /* useVendorCdn */),
    refetchInterval: AIRCRAFT_LOCATION_REFRESH_INTERVAL_MS,
    // If visible and we have active aircraft hex codes
    enabled: visible && Boolean(hexList.length),
  });

  useEffect(() => {
    if (!filteredAircraftsQuery.data) {
      return;
    }
    setPlanePositionCache((cached) =>
      mergeDataToCache(filteredAircraftsQuery.data, cached, true),
    );
  }, [filteredAircraftsQuery.data]);

  // The active aircraft is whichever is hovered, or selected if none is hovered.
  const activeAircraftHexCode =
    hoveredAircraftHexCode || selectedAircraftHexCode;
  const aircraftTracesQuery = useQuery({
    queryKey: ['aircraftTraces', activeAircraftHexCode],
    queryFn: () =>
      fetchAircraftTraces(activeAircraftHexCode, true /* useVendorCdn */),
    // We need to refetch the aircraft traces for the selected aircraft,
    // at the same interval as the filtered aircrafts query
    refetchInterval: AIRCRAFT_TRACE_REFRESH_INTERVAL_MS,
    enabled: Boolean(activeAircraftHexCode),
  });

  const sourceAircraftData = useMemo(() => {
    const aircraftPositions = Object.values(planePositionCache).map(
      (item) => item.data,
    );
    return aircraftsToGeoJson(
      aircraftPositions,
      activeMetadata,
      activeLabelHexCodes,
    );
  }, [planePositionCache, activeMetadata, activeLabelHexCodes]);

  const selectedAircraftPoiInSource = useMemo(() => {
    if (!selectedAircraftHexCode) return undefined;
    return sourceAircraftData.features.find(
      (feat) => feat.properties.hex === selectedAircraftHexCode,
    );
  }, [selectedAircraftHexCode, sourceAircraftData.features]);

  const activeAircraftInSource = useMemo(() => {
    if (!activeAircraftHexCode) return undefined;
    return sourceAircraftData.features.find(
      (feat) => feat.properties.hex === activeAircraftHexCode,
    );
  }, [activeAircraftHexCode, sourceAircraftData.features]);

  const currentAircraftDetails = useRef<AircraftDetails | null>(null);

  // This updates the point of interest popup with live altitude/speed data
  useEffect(() => {
    if (!selectedAircraftPoiInSource || !selectedAircraftPoi) return;
    const item = selectedAircraftPoiInSource;

    const aircraft: AircraftDetails = {
      name: item.properties.name,
      model: item.properties.model,
      tailNum: item.properties.tailNum,
      type: item.properties.type,
      hex: item.properties.hex,
      altitude: item.properties.altitude,
      speed: item.properties.speed,
      squawk: item.properties.squawk,
    };

    if (isEqual(aircraft, currentAircraftDetails.current)) return;

    currentAircraftDetails.current = aircraft;

    setSelectedSingletonPoi({
      type: 'aircraft',
      id: aircraft.hex,
      metadata: aircraft,
    });
  }, [
    selectedAircraftPoiInSource,
    selectedAircraftPoi,
    setSelectedSingletonPoi,
  ]);

  useEffect(() => {
    if (
      !selectedAircraftPoi ||
      prevSelectedAircraftPoi !== selectedAircraftPoi
    ) {
      return;
    }

    // Source data was re-fetch and selected poi is no longer included
    if (!selectedAircraftPoiInSource) {
      clearSelectedPois();
      setSelectedAircraftHexCode('');
    }
  }, [
    selectedAircraftPoiInSource,
    selectedAircraftPoi,
    clearSelectedPois,
    prevSelectedAircraftPoi,
  ]);

  const traceData = useMemo(() => {
    if (!aircraftTracesQuery.data || !activeAircraftHexCode) {
      return {
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'LineString' as const,
          coordinates: [],
        },
      };
    }

    const aircraftPosition = {
      lat: activeAircraftInSource?.geometry?.coordinates[1],
      lng: activeAircraftInSource?.geometry?.coordinates[0],
    };

    const coordinates = (aircraftTracesQuery.data.trace ?? []).map(
      ([_, lat, lng]) => [lng, lat],
    );

    if (hasValidCoordinates(aircraftPosition)) {
      coordinates.push([aircraftPosition.lng, aircraftPosition.lat]);
    }

    return {
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'LineString' as const,
        coordinates,
      },
    };
  }, [
    aircraftTracesQuery.data,
    activeAircraftHexCode,
    activeAircraftInSource?.geometry?.coordinates,
  ]);

  return (
    <>
      <Source
        id={TRACE_SOURCE_ID}
        type="geojson"
        data={traceData}
        maxzoom={12}
        lineMetrics
      >
        <Layer
          id={TRACE_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          {...traceStyle}
        />
      </Source>

      <AircraftDeckLayers
        data={sourceAircraftData}
        onClick={handleClick}
        onHover={handleHover}
        visible={visible}
      />
    </>
  );
};
